import { supabase } from './supabase';
import { AdminRole } from '../contexts/AdminRoleContext';

// Helper function to fix type errors by converting Supabase response types to our model types
export const fixTypes = <T>(data: any): T[] => {
  if (!data) return [];
  return data as T[];
};

// ========= Legacy functions (these will be deprecated) ==========
// These are kept for backward compatibility but should be migrated to use the AdminRoleContext

// Get current user's role from user_roles table
export const getCurrentUserRole = async (): Promise<AdminRole | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    
    if (!userData.user) {
      return null;
    }
    
    // Use any as a workaround to access the from method with 'user_roles'
    const queryBuilder = supabase.from('user_roles') as any;
    
    // Proceed with the query
    const { data, error } = await queryBuilder
      .select('*')
      .eq('user_id', userData.user.id)
      .single();
    
    if (error || !data) {
      console.error('Error fetching user role:', error);
      return null;
    }
    
    // Type cast to ensure it matches our AdminRole interface
    return {
      id: data.id,
      user_id: data.user_id,
      email: data.email,
      role: data.role,
      club_id: data.club_id,
      created_at: data.created_at,
      updated_at: data.updated_at
    } as AdminRole;
  } catch (error) {
    console.error('Error in getCurrentUserRole:', error);
    return null;
  }
};

// Check if the current user is a superadmin
export const isSuperAdmin = async (): Promise<boolean> => {
  const userRole = await getCurrentUserRole();
  return userRole?.role === 'SUPERADMIN';
};

// Get club IDs the current user has access to
export const getUserClubIds = async (): Promise<string[]> => {
  const userRole = await getCurrentUserRole();
  
  if (!userRole) {
    return [];
  }
  
  // Superadmins have access to all clubs
  if (userRole.role === 'SUPERADMIN') {
    const { data } = await supabase
      .from('clubs')
      .select('id');
    
    return data?.map(club => club.id) || [];
  }
  
  // Regular admins only have access to their assigned club
  if (userRole.club_id) {
    return [userRole.club_id];
  }
  
  return [];
};

// Function to apply club-based filtering to a query
export const applyAdminFilter = async (table: string, query: any, userRole?: AdminRole | null) => {
  // IMPORTANT: We don't store a copy of the original query 
  // because Supabase query builder chains return new instances
  
  // Get the user's role to determine filtering if not provided
  if (!userRole) {
    userRole = await getCurrentUserRole();
  }
  
  if (!userRole) {
    // No role found, return empty result
    return query.filter('id', 'eq', '00000000-0000-0000-0000-000000000000');
  }
  
  // Superadmins can access everything
  if (userRole.role === 'SUPERADMIN') {
    return query;
  }
  
  // Regular admins can only access their club's data
  if (userRole.club_id) {
    try {
      switch (table) {
        case 'clubs':
          // For clubs table, only return the admin's assigned club
          return query.eq('id', userRole.club_id);
          
        case 'leagues':
          // For leagues, filter by club_id
          return query.eq('club_id', userRole.club_id);
          
        case 'games':
          // For games, need to join through leagues
          const queryBuilder = supabase.from('leagues') as any;
          const { data: leagues } = await queryBuilder
            .select('id')
            .eq('club_id', userRole.club_id);
            
          const leagueIds = leagues?.map((league: any) => league.id) || [];
          
          if (leagueIds.length === 0) {
            // No leagues, return empty result
            return query.filter('id', 'eq', '00000000-0000-0000-0000-000000000000');
          } else {
            return query.in('league_id', leagueIds);
          }
          
        case 'seating_plans':
          // For seating_plans, also need to join through leagues
          const queryBuilderSeating = supabase.from('leagues') as any;
          const { data: leaguesForSeating } = await queryBuilderSeating
            .select('id')
            .eq('club_id', userRole.club_id);
            
          const leagueIdsForSeating = leaguesForSeating?.map((league: any) => league.id) || [];
          
          if (leagueIdsForSeating.length === 0) {
            // No leagues, return empty result
            return query.filter('id', 'eq', '00000000-0000-0000-0000-000000000000');
          } else {
            return query.in('league_id', leagueIdsForSeating);
          }
        
        case 'materials':
          try {
            // First test if club_id exists on the table by doing a test query
            await supabase
              .from('materials')
              .select('id')
              .eq('club_id', userRole.club_id)
              .limit(1);
              
            // If we got here, club_id exists
            return query.eq('club_id', userRole.club_id);
          } catch (e) {
            // If club_id doesn't exist, don't filter
            return query;
          }
          
        case 'news':
          try {
            // First test if club_id exists on the table by doing a test query
            await supabase
              .from('news')
              .select('id')
              .eq('club_id', userRole.club_id)
              .limit(1);
              
            // If we got here, club_id exists
            return query.eq('club_id', userRole.club_id);
          } catch (e) {
            // If club_id doesn't exist, don't filter
            return query;
          }
          
        case 'players':
          // Players don't have club_id association, so we allow full access
          // This is a reasonable default since players often play in multiple clubs
          return query;
          
        default:
          // For other tables, no filtering by default
          return query;
      }
    } catch (err) {
      console.error(`Error applying admin filter to ${table}:`, err);
      // On error, return the original query without filtering
      return query;
    }
  }
  
  // If admin doesn't have a club assigned, return empty result
  return query.filter('id', 'eq', '00000000-0000-0000-0000-000000000000');
};